import { useGetUsersPreferencesUnitsSuspense } from "@imi/api-client/index.js";
import {
  buildUnitsPreferences,
  UnitsPreferences,
} from "@src/core/api/unitsPreferences";
import {
  PhysicalType,
  physicalTypeToUnitType,
  SinglePhysicalType,
} from "@src/core/engine/units/constants";
import { unitsRepository } from "@src/core/engine/units/unitsRepository";
import { utilsRepository } from "@src/core/engine/utils/utilsRepository";
import { mapValues } from "radashi";
import { useCallback, useMemo } from "react";

const options = unitsRepository.getOptions();
export const defaultUnits = mapValues(options, (value) => value.default);
export const internationalUnits = mapValues(
  options,
  (value) => value.international,
);

export type UnitConversionFn = (
  value: number,
  physicalType: PhysicalType,
) => number;

export function getUnit(
  physicalType: PhysicalType,
  units: UnitsPreferences,
  defaultValues: Record<string, string | null> = {},
) {
  const physicalTypes = physicalType.split(".") as SinglePhysicalType[];
  return physicalTypes
    .map((type) => {
      const unitType = physicalTypeToUnitType[type];
      return units[unitType] || defaultValues[unitType];
    })
    .join(".");
}

export function useUnitConversion() {
  const { data } = useGetUsersPreferencesUnitsSuspense();
  const units = useMemo(() => buildUnitsPreferences(data), [data]);

  const convertToInternationalUnit = useCallback(
    (value: number, physicalType: PhysicalType) => {
      const unitFrom = getUnit(physicalType, units, defaultUnits);
      const unitTo = getUnit(physicalType, internationalUnits);
      if (!unitFrom || !unitTo) return value;
      return utilsRepository.convertUnit(value, unitFrom, unitTo);
    },
    [units],
  );

  const convertToUserUnit = useCallback(
    (value: number, physicalType: PhysicalType) => {
      const unitFrom = getUnit(physicalType, internationalUnits);
      const unitTo = getUnit(physicalType, units, defaultUnits);
      if (!unitFrom || !unitTo) return value;
      return utilsRepository.convertUnit(value, unitFrom, unitTo);
    },
    [units],
  );

  const getUnitSymbol = useCallback(
    (physicalType: PhysicalType | null | undefined) => {
      if (!physicalType) return null;
      const userUnit = getUnit(physicalType, units, defaultUnits);
      if (!userUnit) return null;
      return utilsRepository.getUnitSymbol(userUnit);
    },
    [units],
  );

  return {
    convertToInternationalUnit,
    convertToUserUnit,
    getUnitSymbol,
    units,
  };
}
